import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, User, Building, Mail, MapPin, Briefcase, CheckCircle, XCircle, Edit2, Shield, Crown, Zap } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminUserDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [subscriptionForm, setSubscriptionForm] = useState({
    plan_name: '',
    billing_cycle: 'monthly',
    plan_start_date: '',
    plan_end_date: ''
  });

  const planOptions = {
    founder: ['Founder Free', 'Founder Pro', 'Founder Elite'],
    investor: ['Investor Free', 'Investor Pro', 'Investor Premium'],
    cofounder: ['Co-Founder Free', 'Co-Founder Plus']
  };

  useEffect(() => {
    fetchUserData();
  }, [userId]);

  const fetchUserData = async () => {
    try {
      const response = await axios.get(`${API}/admin/user/${userId}`);
      setUserData(response.data);
      const user = response.data.user;
      setSubscriptionForm({
        plan_name: user.plan_name || planOptions[user.role]?.[0] || '',
        billing_cycle: user.billing_cycle || 'monthly',
        plan_start_date: user.plan_starts_at ? user.plan_starts_at.split('T')[0] : '',
        plan_end_date: user.plan_expires_at ? user.plan_expires_at.split('T')[0] : ''
      });
    } catch (error) {
      toast.error('Failed to load user details');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSubscription = async () => {
    try {
      await axios.post(`${API}/admin/update-user-subscription/${userId}`, {
        plan_name: subscriptionForm.plan_name,
        billing_cycle: subscriptionForm.billing_cycle,
        plan_start_date: subscriptionForm.plan_start_date ? new Date(subscriptionForm.plan_start_date).toISOString() : null,
        plan_end_date: subscriptionForm.plan_end_date ? new Date(subscriptionForm.plan_end_date).toISOString() : null
      });
      toast.success('Subscription updated successfully');
      setEditing(false);
      fetchUserData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update subscription');
    }
  };

  const handleVerifyInvestor = async (verified) => {
    try {
      await axios.post(`${API}/admin/verify-investor/${userId}?verified=${verified}`);
      toast.success(`Investor ${verified ? 'verified' : 'unverified'} successfully`);
      fetchUserData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update verification');
    }
  };

  const handleDeleteUser = async () => {
    if (!window.confirm('Are you sure you want to delete this user? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/user/${userId}`);
      toast.success('User deleted successfully');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const getPlanIcon = () => {
    if (!userData?.user) return <Shield className="text-blue-700" size={20} />;
    const planName = userData.user.plan_name?.toLowerCase() || '';
    if (planName.includes('elite') || planName.includes('premium')) return <Crown className="text-blue-600" size={20} />;
    if (planName.includes('pro') || planName.includes('plus')) return <Zap className="text-blue-600" size={20} />;
    return <Shield className="text-blue-700" size={20} />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100">
        <Navigation />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="text-blue-800">Loading...</div>
        </div>
      </div>
    );
  }

  if (!userData) return null;
  const { user, profile } = userData;

  return (
    <div className="min-h-screen bg-slate-100">
      <Navigation />
      
      <div className="max-w-4xl mx-auto px-6 py-8">
        <Button onClick={() => navigate('/dashboard')} variant="ghost" className="text-blue-700 mb-6">
          <ArrowLeft className="mr-2" size={16} />
          Back to Dashboard
        </Button>

        {/* User Header */}
        <Card className="bg-white border border-slate-200 shadow-sm p-6 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-blue-600/20 flex items-center justify-center">
                <User className="text-blue-600" size={32} />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-blue-900">{user.name}</h1>
                <div className="flex items-center gap-2 mt-1">
                  <Mail className="text-blue-700" size={14} />
                  <span className="text-blue-700">{user.email}</span>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <span className={`text-xs px-2 py-1 rounded capitalize ${
                    user.role === 'founder' ? 'bg-blue-100 text-blue-800' :
                    user.role === 'investor' ? 'bg-green-100 text-green-800' :
                    user.role === 'cofounder' ? 'bg-purple-100 text-purple-800' :
                    'bg-slate-200 text-blue-800'
                  }`}>
                    {user.role}
                  </span>
                  {user.role === 'investor' && (
                    profile?.verified ? (
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded flex items-center gap-1">
                        <CheckCircle size={12} /> Verified
                      </span>
                    ) : (
                      <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded flex items-center gap-1">
                        <XCircle size={12} /> Not Verified
                      </span>
                    )
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              {user.role === 'investor' && (
                <Button
                  onClick={() => handleVerifyInvestor(!profile?.verified)}
                  variant="outline"
                  className={profile?.verified ? 'text-red-400 border-red-400' : 'text-green-400 border-green-400'}
                >
                  {profile?.verified ? 'Unverify' : 'Verify'} Investor
                </Button>
              )}
              <Button onClick={handleDeleteUser} variant="destructive">
                Delete User
              </Button>
            </div>
          </div>
        </Card>

        {/* Subscription Section */}
        <Card className="bg-white border border-slate-200 shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-blue-900 flex items-center gap-2">
              {getPlanIcon()}
              Subscription
            </h2>
            <Button onClick={() => setEditing(!editing)} variant="ghost" className="text-blue-500">
              <Edit2 size={14} className="mr-1" />
              {editing ? 'Cancel' : 'Edit'}
            </Button>
          </div>

          {editing ? (
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-blue-700 text-sm">Plan</Label>
                <Select 
                  value={subscriptionForm.plan_name}
                  onValueChange={(value) => setSubscriptionForm({...subscriptionForm, plan_name: value})}
                >
                  <SelectTrigger className="input-field mt-1">
                    <SelectValue placeholder="Select plan" />
                  </SelectTrigger>
                  <SelectContent>
                    {(planOptions[user.role] || []).map(plan => (
                      <SelectItem key={plan} value={plan}>{plan}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-blue-700 text-sm">Billing Cycle</Label>
                <Select 
                  value={subscriptionForm.billing_cycle}
                  onValueChange={(value) => setSubscriptionForm({...subscriptionForm, billing_cycle: value})}
                >
                  <SelectTrigger className="input-field mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-blue-700 text-sm">Start Date</Label>
                <Input 
                  type="date"
                  value={subscriptionForm.plan_start_date}
                  onChange={(e) => setSubscriptionForm({...subscriptionForm, plan_start_date: e.target.value})}
                  className="input-field mt-1" 
                />
              </div>
              <div>
                <Label className="text-blue-700 text-sm">End Date</Label>
                <Input 
                  type="date"
                  value={subscriptionForm.plan_end_date}
                  onChange={(e) => setSubscriptionForm({...subscriptionForm, plan_end_date: e.target.value})}
                  className="input-field mt-1" 
                />
              </div>
              <div className="md:col-span-2">
                <Button onClick={handleSaveSubscription} className="btn-primary">
                  Save Subscription
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-slate-100/50 p-4 rounded-lg">
                <p className="text-blue-700 text-xs uppercase">Plan</p>
                <p className="text-blue-900 font-medium">{user.plan_name || 'Free'}</p>
              </div>
              <div className="bg-slate-100/50 p-4 rounded-lg">
                <p className="text-blue-700 text-xs uppercase">Billing</p>
                <p className="text-blue-900 font-medium capitalize">{user.billing_cycle || 'monthly'}</p>
              </div>
              <div className="bg-slate-100/50 p-4 rounded-lg">
                <p className="text-blue-700 text-xs uppercase">Status</p>
                <span className={`text-sm px-2 py-1 rounded ${
                  user.plan_status === 'active' ? 'bg-green-500/20 text-green-400' :
                  user.plan_status === 'expired' ? 'bg-red-500/20 text-red-400' :
                  'bg-slate-700 text-blue-800'
                }`}>
                  {user.plan_status || 'free'}
                </span>
              </div>
              <div className="bg-slate-100/50 p-4 rounded-lg">
                <p className="text-blue-700 text-xs uppercase">Expires</p>
                <p className="text-blue-900 font-medium">
                  {user.plan_expires_at ? new Date(user.plan_expires_at).toLocaleDateString() : '—'}
                </p>
              </div>
            </div>
          )}
        </Card>

        {/* Profile Details */}
        {profile && (
          <Card className="bg-white border border-slate-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-blue-900 mb-4 flex items-center gap-2">
              <Briefcase className="text-blue-600" size={20} />
              Profile Details
            </h2>
            <div className="space-y-4">
              {user.role === 'founder' && (
                <>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-slate-100/50 p-4 rounded-lg">
                      <p className="text-blue-700 text-xs uppercase">Startup</p>
                      <p className="text-blue-900 font-medium">{profile.startup_name || '—'}</p>
                    </div>
                    <div className="bg-slate-100/50 p-4 rounded-lg">
                      <p className="text-blue-700 text-xs uppercase">Stage</p>
                      <p className="text-blue-900 font-medium capitalize">{profile.stage || '—'}</p>
                    </div>
                    <div className="bg-slate-100/50 p-4 rounded-lg">
                      <p className="text-blue-700 text-xs uppercase">Location</p>
                      <p className="text-blue-900 font-medium">{profile.city}, {profile.country}</p>
                    </div>
                    <div className="bg-slate-100/50 p-4 rounded-lg">
                      <p className="text-blue-700 text-xs uppercase">Funding Ask</p>
                      <p className="text-blue-900 font-medium">${profile.funding_asked_min?.toLocaleString()} - ${profile.funding_asked_max?.toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="bg-slate-100/50 p-4 rounded-lg">
                    <p className="text-blue-700 text-xs uppercase">One-Line Pitch</p>
                    <p className="text-blue-900">{profile.one_line_pitch || '—'}</p>
                  </div>
                </>
              )}
              {user.role === 'investor' && (
                <>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-slate-100/50 p-4 rounded-lg">
                      <p className="text-blue-700 text-xs uppercase">Type</p>
                      <p className="text-blue-900 font-medium capitalize">{profile.investor_type || '—'}</p>
                    </div>
                    <div className="bg-slate-100/50 p-4 rounded-lg">
                      <p className="text-blue-700 text-xs uppercase">Location</p>
                      <p className="text-blue-900 font-medium">{profile.city}, {profile.country}</p>
                    </div>
                    <div className="bg-slate-100/50 p-4 rounded-lg">
                      <p className="text-blue-700 text-xs uppercase">Ticket Size</p>
                      <p className="text-blue-900 font-medium">${profile.ticket_size_min?.toLocaleString()} - ${profile.ticket_size_max?.toLocaleString()}</p>
                    </div>
                    <div className="bg-slate-100/50 p-4 rounded-lg">
                      <p className="text-blue-700 text-xs uppercase">Preferred Stages</p>
                      <p className="text-blue-900 font-medium">{profile.preferred_stages?.join(', ') || '—'}</p>
                    </div>
                  </div>
                  <div className="bg-slate-100/50 p-4 rounded-lg">
                    <p className="text-blue-700 text-xs uppercase">Bio</p>
                    <p className="text-blue-900">{profile.bio || '—'}</p>
                  </div>
                </>
              )}
              {user.role === 'cofounder' && (
                <>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-slate-100/50 p-4 rounded-lg">
                      <p className="text-blue-700 text-xs uppercase">Skills</p>
                      <p className="text-blue-900 font-medium">{profile.skills?.join(', ') || '—'}</p>
                    </div>
                    <div className="bg-slate-100/50 p-4 rounded-lg">
                      <p className="text-blue-700 text-xs uppercase">Looking For</p>
                      <p className="text-blue-900 font-medium">{profile.looking_for?.join(', ') || '—'}</p>
                    </div>
                    <div className="bg-slate-100/50 p-4 rounded-lg">
                      <p className="text-blue-700 text-xs uppercase">Location</p>
                      <p className="text-blue-900 font-medium">{profile.city}, {profile.country}</p>
                    </div>
                    <div className="bg-slate-100/50 p-4 rounded-lg">
                      <p className="text-blue-700 text-xs uppercase">Industries</p>
                      <p className="text-blue-900 font-medium">{profile.industries?.join(', ') || '—'}</p>
                    </div>
                  </div>
                  <div className="bg-slate-100/50 p-4 rounded-lg">
                    <p className="text-blue-700 text-xs uppercase">Bio</p>
                    <p className="text-blue-900">{profile.bio || '—'}</p>
                  </div>
                </>
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
